/**
 * useTemplateLibrary Hook
 *
 * Manages template library state including search, filtering,
 * and template selection for adding to user's templates.
 */

import { useCallback, useMemo, useState } from 'react';

import type { AdminPublicTemplate, AvailableModule } from '../types';

interface UseTemplateLibraryProps {
  /** Available public templates */
  publicTemplates: AdminPublicTemplate[];
  /** Template IDs already in user's list */
  excludedTemplateIds: string[];
  /** Available modules for preview info */
  availableModules?: AvailableModule[];
}

interface UseTemplateLibraryReturn {
  /** Filtered templates based on search and category */
  filteredTemplates: AdminPublicTemplate[];
  /** Current search term */
  searchTerm: string;
  /** Current category filter */
  categoryFilter: string | null;
  /** Available categories */
  categories: string[];
  /** Set search term */
  setSearchTerm: (term: string) => void;
  /** Set category filter */
  setCategoryFilter: (category: string | null) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Number of results */
  resultCount: number;
}

export const useTemplateLibrary = ({
  publicTemplates,
  excludedTemplateIds,
  availableModules: _availableModules,
}: UseTemplateLibraryProps): UseTemplateLibraryReturn => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Extract unique categories from templates
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    publicTemplates.forEach(template => {
      if (template.public_categories) {
        template.public_categories.forEach(category => categorySet.add(category));
      }
    });
    return Array.from(categorySet).sort();
  }, [publicTemplates]);

  // Filter templates based on search term, category, and exclusions
  const filteredTemplates = useMemo(() => {
    return publicTemplates.filter(template => {
      // Exclude already added templates
      if (excludedTemplateIds.includes(template.template_id)) {
        return false;
      }

      // Must have a public name
      if (!template.public_name || template.public_name.length === 0) {
        return false;
      }

      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = template.public_name?.toLowerCase().includes(searchLower);
        const descMatch = template.public_description?.toLowerCase().includes(searchLower);
        const sectionMatch = template.sections?.some(section =>
          section.display_name?.toLowerCase().includes(searchLower),
        );

        if (!nameMatch && !descMatch && !sectionMatch) {
          return false;
        }
      }

      // Category filter
      if (categoryFilter) {
        if (
          !template.public_categories ||
          !template.public_categories.includes(categoryFilter)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [publicTemplates, excludedTemplateIds, searchTerm, categoryFilter]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setCategoryFilter(null);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = searchTerm.length > 0 || categoryFilter !== null;

  return {
    filteredTemplates,
    searchTerm,
    categoryFilter,
    categories,
    setSearchTerm,
    setCategoryFilter,
    clearFilters,
    hasActiveFilters,
    resultCount: filteredTemplates.length,
  };
};
